import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  X,
  Wrench,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { customersAPI } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { displayUpperCase } from '../utils/textUtils';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useFormDraft } from '../hooks/useFormDraft';
import { supabaseWithRetry, getUserFriendlyErrorMessage } from '../utils/retryApi';

const ProductOperations = () => {
  const { customerId, operationId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useNotification();
  const { isWarehouse } = useAuth();

  // State management
  const [operation, setOperation] = useState(null);
  const [operationDetails, setOperationDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [editingDetail, setEditingDetail] = useState(null);
  const [selectedDetailForCost, setSelectedDetailForCost] = useState(null);

  // Form data
  const [detailFormData, setDetailFormData] = useState({
    operationType: '',
    operationDescription: ''
  });

  // Cost form data
  const [costFormData, setCostFormData] = useState({
    cost: '',
    description: ''
  });
  const [costInput, setCostInput] = useState('');

  // Balance transactions for each detail
  const [balanceTransactions, setBalanceTransactions] = useState({});
  const [operationSubmitting, setOperationSubmitting] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Network status hook
  const { isOnline } = useOnlineStatus();

  // Form draft for cost form
  const { draft: costDraft, updateDraft: updateCostDraft, clearDraft: clearCostDraft, isDirty: costDirty } = useFormDraft(
    `cost:${customerId}:${operationId}`,
    { cost: '', description: '' },
    { autoSave: true, saveDelay: 500 }
  );

  // Fetch operation details
  const fetchOperation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get product operations for the customer
      const operationsResponse = await customersAPI.getProductOperations(customerId);
      const operation = operationsResponse.data.operations.find(op => op.id === operationId);

      if (!operation) {
        setError('İşlem bulunamadı');
        return;
      }

      setOperation(operation);

      // Get operation details
      const detailsResponse = await customersAPI.getProductOperationDetails(operationId);
      setOperationDetails(detailsResponse.data.details);

      // Fetch balance transactions for each detail (only if not warehouse user)
      if (!isWarehouse()) {
        const balanceData = {};
        for (const detail of detailsResponse.data.details || []) {
          try {
            console.log(`Fetching balance transactions for detail ${detail.id}`);
            const balanceResponse = await customersAPI.getBalanceTransactionsByDetail(detail.id);
            balanceData[detail.id] = balanceResponse.data.transactions || [];
            console.log(`Found ${balanceData[detail.id].length} balance transactions for detail ${detail.id}`);
          } catch (err) {
            console.error(`Error fetching balance for detail ${detail.id}:`, err);
            balanceData[detail.id] = [];
          }
        }
        console.log('All balance data:', balanceData);
        setBalanceTransactions(balanceData);
      }
    } catch (err) {
      console.error('Error fetching operation:', err);
      setError('İşlem bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [customerId, operationId, isWarehouse]);

  useEffect(() => {
    fetchOperation();
  }, [fetchOperation]);

  // Reset pagination when operation details change
  useEffect(() => {
    setCurrentPage(1);
  }, [operationDetails]);

  // Pagination helper functions
  const totalPages = Math.ceil(operationDetails.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDetails = operationDetails.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of operations list
    const operationsList = document.getElementById('operations-list');
    if (operationsList) {
      operationsList.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Handle form submission
  const handleDetailSubmit = async (e) => {
    e.preventDefault();

    if (!detailFormData.operationType) {
      showWarning('İşlem türü alanı zorunludur');
      return;
    }

    try {
      setOperationSubmitting(true);

      if (editingDetail) {
        await customersAPI.updateProductOperationDetail(editingDetail.id, detailFormData);
        showSuccess('İşlem detayı başarıyla güncellendi');
        setShowEditModal(false);
      } else {
        await customersAPI.addProductOperationDetail(operationId, detailFormData);
        showSuccess('İşlem detayı başarıyla eklendi');
        setShowDetailModal(false);
      }

      // Reset form and close modals
      setDetailFormData({
        operationType: '',
        operationDescription: ''
      });
      setEditingDetail(null);

      // Refresh data and reset pagination
      fetchOperation();
      setCurrentPage(1);
    } catch (err) {
      console.error('Detail operation error:', err);
      showError('İşlem detayı işlenemedi: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setOperationSubmitting(false);
    }
  };

  // Handle edit detail
  const handleEditDetail = (detail) => {
    setEditingDetail(detail);
    setDetailFormData({
      operationType: detail.operationType,
      operationDescription: detail.operationDescription
    });
    setShowEditModal(true);
  };

  // Handle delete detail
  const handleDeleteDetail = async (detailId) => {
    if (window.confirm('Bu işlem detayını silmek istediğinizden emin misiniz?')) {
      try {
        setOperationSubmitting(true);
        await customersAPI.deleteProductOperationDetail(detailId);
        showSuccess('İşlem detayı başarıyla silindi');
        fetchOperation();

        // If current page becomes empty after deletion, go to previous page
        const remainingItems = operationDetails.length - 1;
        const remainingPages = Math.ceil(remainingItems / itemsPerPage);
        if (currentPage > remainingPages && remainingPages > 0) {
          setCurrentPage(remainingPages);
        } else if (remainingPages === 0) {
          setCurrentPage(1);
        }
      } catch (err) {
        console.error('Delete detail error:', err);
        showError('İşlem detayı silinemedi: ' + (err.message || 'Bilinmeyen hata'));
      } finally {
        setOperationSubmitting(false);
      }
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (detail) => {
    try {
      setOperationSubmitting(true);
      console.log('Toggle status called for detail:', detail);
      const updatedDetail = {
        ...detail,
        isActive: !detail.isActive
      };
      console.log('Updated detail data:', updatedDetail);
      await customersAPI.updateProductOperationDetail(detail.id, updatedDetail);
      console.log('Successfully updated, refreshing data...');
      await fetchOperation();
      showSuccess('Durum başarıyla güncellendi');
    } catch (error) {
      console.error('Durum güncellenemedi:', error);
      showError('Durum güncellenemedi: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setOperationSubmitting(false);
    }
  };

  // Handle form input change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setDetailFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle balance form submission (only for non-warehouse users) - İyileştirilmiş versiyon
  const handleBalanceSubmit = async (e) => {
    if (isWarehouse()) return;

    e.preventDefault();

    // Online/offline kontrolü
    if (!isOnline) {
      showWarning('İnternet bağlantısı yok. Lütfen bağlantınızı kontrol edin ve tekrar deneyin.');
      return;
    }

    // Form validation
    const currentCost = costDraft.cost || costFormData.cost;
    const currentDescription = costDraft.description || costFormData.description;

    if (!currentCost || !currentDescription) {
      showWarning('Tutar ve açıklama alanları zorunludur');
      return;
    }

    try {
      setOperationSubmitting(true);

      // Draft varsa kullanıcıya sor
      const pendingDraft = localStorage.getItem(`draft:cost:${customerId}:${operationId}`);
      if (pendingDraft && costDirty) {
        const hasConfirmed = window.confirm(
          'Daha önce kaydedilmemiş veriler bulundu. Bu verileri kullanmak istiyor musunuz?\n\n' +
          'İptal ederseniz yeni verileriniz kaydedilecek.'
        );
        if (!hasConfirmed) {
          // Yeni verileri draft'a kaydet
          updateCostDraft({
            cost: currentCost,
            description: currentDescription
          });
          return;
        }
      }

      const transactionData = {
        type: 'payment',
        amount: parseFloat(currentCost),
        date: new Date().toISOString().split('T')[0],
        description: currentDescription,
        notes: `Bakiye ${operation.brand} ${operation.productName} ${operation.model || ''} - ${selectedDetailForCost.operationType}`.trim(),
        detail_id: selectedDetailForCost.id
      };

      console.log('Adding balance transaction with retry:', transactionData);

      // Retry logic ile API çağrısı
      await supabaseWithRetry(
        () => customersAPI.addBalanceTransaction(customerId, transactionData),
        {
          maxRetries: 3,
          initialDelay: 1000,
          backoffMultiplier: 2
        }
      );

      console.log('Balance transaction added successfully');
      showSuccess('Bakiye işlemi başarıyla eklendi');

      // Reset form and close modal
      setCostFormData({
        cost: '',
        description: ''
      });
      setCostInput('');
      setShowCostModal(false);
      setSelectedDetailForCost(null);

      // Clear draft on success
      clearCostDraft();

      // Refresh operation details
      fetchOperation();
    } catch (err) {
      console.error('Balance transaction error:', err);

      const errorMessage = getUserFriendlyErrorMessage(err);
      showError(`Bakiye işlemi eklenemedi: ${errorMessage}`);

      // İnternet hatası durumunda draft'ı koru
      if (!isOnline || err.code === 'NETWORK_ERROR') {
        showWarning('Verileriniz yerel olarak kaydedildi. İnternet bağlantınız geri geldiğinde otomatik olarak gönderilecek.');
      }
    } finally {
      setOperationSubmitting(false);
    }
  };

  // Handle cost input change
  const handleCostChange = (e) => {
    const value = e.target.value.replace(/[^0-9.,]/g, '');
    setCostInput(value);

    // Convert to number for form data
    const numericValue = value.replace(',', '.');
    setCostFormData(prev => ({
      ...prev,
      cost: numericValue
    }));
  };

  // Handle cost input blur
  const handleCostBlur = () => {
    if (costInput) {
      const numericValue = parseFloat(costInput.replace(',', '.'));
      if (!isNaN(numericValue)) {
        setCostInput(numericValue.toFixed(2).replace('.', ','));
        setCostFormData(prev => ({
          ...prev,
          cost: numericValue.toString()
        }));
      }
    }
  };

  // Handle add balance button click (only for non-warehouse users) - Draft destekli
  const handleAddBalance = (detail) => {
    if (isWarehouse()) return;

    setSelectedDetailForCost(detail);

    // Draft'tan veri yükle veya boş başlat
    const draftCost = costDraft.cost || '';
    const draftDescription = costDraft.description || '';

    setCostFormData({
      cost: draftCost,
      description: draftDescription
    });

    // Cost input formatını güncelle
    setCostInput(draftCost ? draftCost.toString().replace('.', ',') : '');

    setShowCostModal(true);
  };

  // Handle delete balance transaction (only for non-warehouse users)
  const handleDeleteBalance = async (transactionId) => {
    if (isWarehouse()) return;

    if (window.confirm('Bu bakiye işlemini silmek istediğinizden emin misiniz?')) {
      try {
        setOperationSubmitting(true);
        await customersAPI.deleteBalanceTransaction(transactionId);
        showSuccess('Bakiye işlemi başarıyla silindi');
        fetchOperation();
      } catch (err) {
        console.error('Delete balance error:', err);
        showError('Bakiye işlemi silinemedi: ' + (err.message || 'Bilinmeyen hata'));
      } finally {
        setOperationSubmitting(false);
      }
    }
  };

  // Handle modal close
  const handleCloseModals = () => {
    setShowDetailModal(false);
    setShowEditModal(false);
    setShowCostModal(false);
    setEditingDetail(null);
    setSelectedDetailForCost(null);
    setDetailFormData({
      operationType: '',
      operationDescription: ''
    });
    setCostFormData({
      cost: '',
      description: ''
    });
    setCostInput('');
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'closed': return 'text-gray-600 bg-gray-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'in_progress': return <Clock className="h-4 w-4" />;
      case 'cancelled': return <AlertCircle className="h-4 w-4" />;
      case 'closed': return <X className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // Determine operation status based on details
  const getOperationStatus = () => {
    if (operationDetails.length === 0) {
      return 'closed';
    }

    const hasActiveOperations = operationDetails.some(detail => detail.isActive);
    if (hasActiveOperations) {
      return 'in_progress';
    }

    return 'completed';
  };


  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">İşlem bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !operation) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Hata</h2>
          <p className="text-gray-600 mb-4">{error || 'İşlem bulunamadı'}</p>
          <button
            onClick={() => navigate(`/customers/${customerId}?tab=products`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Ürün İşlemlerine Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/customers/${customerId}?tab=products`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{displayUpperCase(operation.productName)}</h1>
              <p className="text-sm text-gray-500">{displayUpperCase(operation.brand)} - {displayUpperCase(operation.model) || 'MODEL BELİRTİLMEMİŞ'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowDetailModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Yeni İşlem</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Full Screen */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="h-full flex flex-col space-y-3">
          {/* Product Info - Ultra Compact */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">Ürün Bilgileri</h3>
              <div className="flex justify-center">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-base font-semibold ${getStatusColor(getOperationStatus())}`}>
                  {getStatusIcon(getOperationStatus())}
                  <span className="ml-2">
                    {getOperationStatus() === 'closed' ? 'Kapalı' :
                      getOperationStatus() === 'in_progress' ? 'Devam Ediyor' :
                        getOperationStatus() === 'completed' ? 'Tamamlandı' :
                          getOperationStatus() === 'cancelled' ? 'İptal Edildi' : 'Bilinmiyor'}
                  </span>
                </span>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div className="bg-white rounded p-3">
                <p className="text-sm text-gray-500 mb-1">Marka</p>
                <p className="font-semibold text-gray-900 text-base">{displayUpperCase(operation.brand)}</p>
              </div>
              <div className="bg-white rounded p-3">
                <p className="text-sm text-gray-500 mb-1">Model</p>
                <p className="font-semibold text-gray-900 text-base">{displayUpperCase(operation.model) || '-'}</p>
              </div>
              <div className="bg-white rounded p-3">
                <p className="text-sm text-gray-500 mb-1">Seri No</p>
                <p className="font-semibold text-gray-900 text-base">{displayUpperCase(operation.serialNumber) || '-'}</p>
              </div>
              <div className="bg-white rounded p-3">
                <p className="text-sm text-gray-500 mb-1">İşlem</p>
                <p className="font-semibold text-gray-900 text-base">{operationDetails.length}</p>
              </div>
            </div>
            {operation.notes && (
              <div className="mt-3 p-3 bg-blue-50 rounded text-sm">
                <p className="text-blue-600 font-medium mb-1">Notlar</p>
                <p className="text-blue-800 text-sm">{displayUpperCase(operation.notes)}</p>
              </div>
            )}
          </div>

          {/* Operations List - Ultra Compact */}
          <div id="operations-list" className="flex-1 bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-white rounded-t-lg flex-shrink-0">
              <h3 className="text-sm font-semibold text-gray-900">Yapılan İşlemler</h3>
              <span className="text-xs text-gray-500">
                {operationDetails.length > 0 ? `${startIndex + 1}-${Math.min(endIndex, operationDetails.length)} / ${operationDetails.length}` : '0'} işlem
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {operationDetails.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Wrench className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs">Henüz yapılan işlem bulunmuyor</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {currentDetails.map((detail, index) => (
                    <div key={detail.id || index} className="bg-white border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-bold bg-blue-100 text-blue-800 border border-blue-200 shadow-sm">
                              {displayUpperCase(detail.operationType)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(detail.created_at || detail.operationDate).toLocaleDateString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })} {new Date(detail.created_at || detail.operationDate).toLocaleTimeString('tr-TR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-2 font-medium">
                            {displayUpperCase(detail.operationDescription) || 'AÇIKLAMA BELİRTİLMEMİŞ'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={() => handleToggleStatus(detail)}
                            disabled={operationSubmitting}
                            className={`w-8 h-4 rounded-full transition-colors ${detail.isActive
                              ? 'bg-green-500 hover:bg-green-600'
                              : 'bg-red-500 hover:bg-red-600'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={detail.isActive ? 'Açık - Kapatmak için tıklayın' : 'Kapalı - Açmak için tıklayın'}
                          >
                            <div className={`w-3 h-3 bg-white rounded-full transition-transform ${detail.isActive ? 'translate-x-4' : 'translate-x-0.5'
                              }`}></div>
                          </button>
                          <div className="flex items-center space-x-0.5">
                            <button
                              onClick={() => handleEditDetail(detail)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                              title="Düzenle"
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteDetail(detail.id)}
                              disabled={operationSubmitting}
                              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Sil"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Balance Transactions - Show only for non-warehouse users */}
                      {!isWarehouse() && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-2 bg-gray-100 rounded-lg p-3 shadow-sm">
                            <h4 className="text-base font-bold text-gray-800">Bakiye İşlemleri</h4>
                            <button
                              onClick={() => handleAddBalance(detail)}
                              disabled={operationSubmitting}
                              className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Bakiye İşlemi Ekle"
                            >
                              <Plus className="h-4 w-4" />
                              <span>{operationSubmitting ? 'İşleniyor...' : 'Ekle'}</span>
                            </button>
                          </div>
                          {balanceTransactions[detail.id] && balanceTransactions[detail.id].length > 0 ? (
                            <div className="space-y-1">
                              {balanceTransactions[detail.id].map((transaction, tIndex) => (
                                <div
                                  key={transaction.id || tIndex}
                                  className="group flex items-center gap-3 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors shadow-sm"
                                >
                                  <span className="text-sm text-gray-500 w-20 font-medium">
                                    {new Date(transaction.created_at || transaction.transaction_date).toLocaleDateString('tr-TR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })}
                                  </span>
                                  <span className="text-sm font-bold text-green-600 w-24">
                                    +{parseFloat(transaction.amount).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                  </span>
                                  <span className="flex-1 text-sm text-gray-700 truncate font-semibold">
                                    {displayUpperCase(transaction.description)}
                                  </span>
                                  <button
                                    onClick={() => handleDeleteBalance(transaction.id)}
                                    disabled={operationSubmitting}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Sil"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-2 text-gray-400 text-xs bg-gray-50 rounded border border-dashed border-gray-200">
                              <p>Henüz bakiye işlemi eklenmemiş</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs text-gray-700">
                      <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, operationDetails.length)}</span> arası,
                      toplam <span className="font-medium">{operationDetails.length}</span> işlem
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-200 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Önceki</span>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {getPageNumbers().map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-3 py-1.5 border text-xs font-medium ${currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-200 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Sonraki</span>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>

                {/* Mobile pagination - simplified */}
                <div className="sm:hidden flex items-center justify-between w-full">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-1.5 border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                  >
                    Önceki
                  </button>
                  <span className="text-xs text-gray-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-1.5 border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                  >
                    Sonraki
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Detail Modal */}
      {(showDetailModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingDetail ? 'İşlem Detayı Düzenle' : 'Yeni İşlem Detayı'}
                </h2>
                <button
                  onClick={handleCloseModals}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleDetailSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İşlem Türü <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="operationType"
                  value={detailFormData.operationType}
                  onChange={handleFormChange}
                  required
                  placeholder="Örn: Tamir, Bakım, Değişim, Temizlik..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İşlem Açıklaması
                </label>
                <textarea
                  name="operationDescription"
                  value={detailFormData.operationDescription}
                  onChange={handleFormChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>



              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModals}
                  className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={operationSubmitting}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {operationSubmitting ? 'İşleniyor...' : (editingDetail ? 'Güncelle' : 'Kaydet')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cost Modal - Only show for non-warehouse users */}
      {!isWarehouse() && showCostModal && selectedDetailForCost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  Bakiye İşlemi Ekle - {selectedDetailForCost.operationType}
                </h2>
                <button
                  onClick={() => setShowCostModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <form onSubmit={handleBalanceSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={costFormData.description}
                  onChange={(e) => setCostFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ücret açıklaması girin"
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ücret <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    value={costInput}
                    onChange={handleCostChange}
                    onBlur={handleCostBlur}
                    placeholder="0,00"
                    required
                    className="w-full pr-10 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right text-lg font-semibold"
                  />
                  <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">₺</span>
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 font-medium">İşlem Bilgisi:</p>
                <p className="text-sm text-blue-700">{selectedDetailForCost.operationType}</p>
                <p className="text-xs text-blue-600">ÜRÜN: {displayUpperCase(operation?.productName)} - {displayUpperCase(operation?.brand)}</p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCostModal(false)}
                  className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={operationSubmitting}
                  className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {operationSubmitting ? 'İşleniyor...' : 'Ücret Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductOperations;